import {Injectable} from '@angular/core';
import {HttpClient, HttpContext} from '@angular/common/http';
import {ActivatedRouteSnapshot, NavigationEnd, Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {filter, map} from 'rxjs/operators';
import {ApiClientService} from '../api-client/api-client.service';
import {AuthService} from '../auth/auth.service';
import {SILENT_ERRORS} from '../../http-interceptors/error.interceptor';

export type UserActivityType = 'navigation' | 'filter' | 'download' | 'logout';
export type ActivityFilters = Record<string, string | number | boolean | (string | number)[]>;

interface PendingVisit {
  url: string;
  title: string | null;
  filters: ActivityFilters | null;
  timer: ReturnType<typeof setTimeout>;
}

interface ActivityPayload {
  url: string;
  title?: string | null;
  filename?: string;
  filters?: ActivityFilters | null;
  changes?: Record<string, any>;
}

const VISIT_FILTERS_WAIT_MS = 3000;
const MAX_LENGTH = 255;
const IGNORED_FILTER_KEYS = ['page', 'count', 'limit', 'offset', 'sort', 'order', 'groups', 'headers', 'withHeaders', 'token'];

@Injectable({
  providedIn: 'root'
})
export class UserActivityService {
  private started = false;
  private currentPath: string | null = null;
  private currentTitle: string | null = null;
  private pendingVisit: PendingVisit | null = null;
  private currentFilters: ActivityFilters | null = null;

  constructor(
    private http: HttpClient,
    private apiClient: ApiClientService,
    private auth: AuthService,
    private router: Router,
    private title: Title,
  ) {
  }

  trackNavigation(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => (event as NavigationEnd).urlAfterRedirects),
    ).subscribe((url) => this.onNavigation(url));
  }

  reportFilters(params: Record<string, any>): void {
    const filters = this.normalizeFilters(params);

    if (this.pendingVisit && this.pendingVisit.filters === null) {
      this.pendingVisit.filters = filters;
      this.currentFilters = filters;
      this.flushPendingVisit();
      return;
    }

    if (this.currentFilters === null) {
      this.currentFilters = filters;
      return;
    }

    const changes = this.diffFilters(this.currentFilters, filters);
    if (!Object.keys(changes).length) {
      return;
    }

    this.currentFilters = filters;
    this.send('filter', {url: this.router.url, title: this.currentTitle, filters, changes});
  }

  logDownload(filename: string, params: Record<string, any> | null = null): void {
    this.flushPendingVisit();

    this.send('download', {
      url: this.router.url,
      title: this.currentTitle,
      filename,
      filters: params ? this.normalizeFilters(params) : this.currentFilters,
    });
  }

  logLogout(): void {
    this.flushPendingVisit();
    this.send('logout', {url: this.router.url});
  }

  private onNavigation(url: string): void {
    const path = url.split('?')[0];
    if (path === this.currentPath) {
      return;
    }

    this.flushPendingVisit();
    this.currentPath = path;
    this.currentTitle = null;
    this.currentFilters = null;

    if (path.startsWith('/login')) {
      return;
    }

    const visit: PendingVisit = {
      url,
      title: null,
      filters: null,
      timer: setTimeout(() => this.flushPendingVisit(), VISIT_FILTERS_WAIT_MS),
    };
    this.pendingVisit = visit;

    setTimeout(() => {
      visit.title = this.getPageLabel();
      if (this.currentPath === path) {
        this.currentTitle = visit.title;
      }
    });
  }

  private flushPendingVisit(): void {
    const visit = this.pendingVisit;
    if (!visit) {
      return;
    }

    clearTimeout(visit.timer);
    this.pendingVisit = null;

    this.send('navigation', {
      url: visit.url,
      title: visit.title ?? this.getPageLabel(),
      filters: visit.filters,
    });
  }

  private getPageLabel(): string | null {
    const title = this.title.getTitle().replace(/ - Oktopus$/, '');
    const ids = this.collectIds(this.router.routerState.snapshot.root);
    const label = [title, ...ids.map(id => '#' + id)].filter(part => part.length).join(' ');

    return label.length ? label : null;
  }

  private collectIds(snapshot: ActivatedRouteSnapshot): string[] {
    const ids = Object.values(snapshot.params).filter(value => /^\d+$/.test(String(value)));
    const childIds = snapshot.firstChild ? this.collectIds(snapshot.firstChild) : [];

    return [...new Set([...ids, ...childIds])];
  }

  private normalizeFilters(params: Record<string, any>): ActivityFilters {
    const filters: ActivityFilters = {};

    for (const key of Object.keys(params).sort()) {
      if (IGNORED_FILTER_KEYS.includes(key)) {
        continue;
      }

      const value = this.normalizeValue(params[key]);
      if (value !== null) {
        filters[key] = value;
      }
    }

    return filters;
  }

  private normalizeValue(value: any): string | number | boolean | (string | number)[] | null {
    if (value instanceof Date) {
      return value.toISOString();
    }

    if (Array.isArray(value)) {
      const items = value
        .map(item => item !== null && typeof item === 'object' ? item.id ?? null : item)
        .filter(item => item !== null && item !== undefined && item !== '');

      return items.length ? items : null;
    }

    if (value !== null && typeof value === 'object') {
      return value.id ?? null;
    }

    if (value === null || value === undefined || value === '' || value === false) {
      return null;
    }

    return typeof value === 'string' ? this.truncate(value) : value;
  }

  private diffFilters(previous: ActivityFilters, next: ActivityFilters): Record<string, any> {
    const changes: Record<string, any> = {};
    const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);

    for (const key of keys) {
      if (JSON.stringify(previous[key]) !== JSON.stringify(next[key])) {
        changes[key] = next[key] ?? null;
      }
    }

    return changes;
  }

  private send(type: UserActivityType, payload: ActivityPayload): void {
    if (!this.auth.isLogged()) {
      return;
    }

    const body: Record<string, any> = {type, url: this.truncate(payload.url)};
    if (payload.title) {
      body['title'] = this.truncate(payload.title);
    }
    if (payload.filename) {
      body['filename'] = this.truncate(payload.filename);
    }
    if (payload.filters && Object.keys(payload.filters).length) {
      body['filters'] = payload.filters;
    }
    if (payload.changes) {
      body['changes'] = payload.changes;
    }

    this.http.post(this.apiClient.getUrl('/user-event/activity'), body, {
      context: new HttpContext().set(SILENT_ERRORS, true),
    }).subscribe({error: () => null});
  }

  private truncate(value: string): string {
    return value.length > MAX_LENGTH ? value.substring(0, MAX_LENGTH) : value;
  }
}
